import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  DollarSign, CreditCard, AlertCircle, Clock, Server, Globe2, Wrench,
  PauseCircle, ShieldOff, Plus, Send, RefreshCcw, PowerOff, Play,
  Bell, Mail, X, Search, CheckCircle2, ListFilter, Sparkles, RotateCw,
} from "lucide-react";

// -------------------- Categories (grouped for the modal) --------------------
const CATEGORY_GROUPS = [
  { label: "Website services", options: [
    ["website_design", "Website Design"], ["website_creation", "Website Creation"],
    ["website_development", "Website Development"], ["website_redesign", "Website Redesign"],
    ["ecommerce_website", "E-Commerce Website"], ["landing_page", "Landing Page"],
    ["website_maintenance", "Website Maintenance"], ["website_hosting", "Website Hosting"],
    ["website_security", "Website Security / SSL"], ["domain_management", "Domain Management"],
    ["domain_renewal", "Domain Renewal"],
  ]},
  { label: "SEO & Google", options: [
    ["seo", "SEO"], ["local_seo", "Local SEO"], ["gbp", "Google Business Profile"],
    ["gbp_management", "GBP Management"], ["google_search_optimization", "Google Search Optimization"],
    ["google_ranking", "Google Ranking / Visibility"], ["seo_audit", "SEO Audit"],
    ["analytics_reporting", "Analytics / Reporting"],
  ]},
  { label: "Marketing", options: [
    ["digital_marketing", "Digital Marketing"], ["social_media", "Social Media"],
    ["social_media_management", "Social Media Management"],
    ["ad_campaign_management", "Ad Campaign Management"], ["meta_ads", "Meta Ads"],
    ["google_ads", "Google Ads"], ["email_marketing", "Email Marketing"],
    ["sms_marketing", "SMS Marketing"], ["marketing_consulting", "Marketing Consulting"],
  ]},
  { label: "Design", options: [
    ["graphic_design", "Graphic Design"], ["logo_design", "Logo Design"],
    ["branding", "Branding / Brand Identity"], ["flyers_design", "Flyers / Advertising"],
    ["business_cards", "Business Cards / Print"], ["social_media_graphics", "Social Media Graphics"],
    ["digital_branding", "Digital Branding"],
  ]},
  { label: "Content & Media", options: [
    ["video_editing", "Video Editing"], ["video_production", "Video Production"],
    ["photography", "Photography"], ["content_creation", "Content Creation"],
    ["copywriting", "Copywriting"], ["social_media_content", "Social Media Content"],
  ]},
  { label: "AI & Automation", options: [
    ["ai_automation", "AI Automation"], ["ai_chatbot", "AI Chatbot"],
    ["ai_assistant", "AI Assistant"], ["business_automation", "Business Automation"],
    ["workflow_automation", "Workflow Automation"], ["lead_automation", "Lead Automation"],
    ["appointment_automation", "Appointment Automation"],
  ]},
  { label: "Software & Systems", options: [
    ["crm", "CRM"], ["crm_setup", "CRM Setup"], ["crm_management", "CRM Management"],
    ["custom_software_development", "Custom Software Development"],
    ["mobile_app_development", "Mobile App Development"], ["web_app_development", "Web App Development"],
    ["client_portal", "Client Portal"], ["business_portal", "Business Portal"],
    ["internal_business_system", "Internal Business System"],
  ]},
  { label: "Support", options: [
    ["technical_support", "Technical Support"], ["support_plan", "Support Plan"],
    ["website_support", "Website Support"], ["system_support", "System Support"],
    ["maintenance_plan", "Maintenance Plan"],
  ]},
  { label: "Other", options: [
    ["consulting", "Consulting"], ["custom_service", "Custom Service"], ["other", "Other"],
    // legacy keys still visible so existing rows show a sensible label
    ["hosting", "Hosting (legacy)"], ["domain", "Domain (legacy)"],
    ["maintenance", "Maintenance (legacy)"],
  ]},
];
const CATEGORY_LABELS = Object.fromEntries(
  CATEGORY_GROUPS.flatMap((g) => g.options)
);

const FREQUENCIES = ["monthly", "quarterly", "semi_annual", "annual", "custom"];
const BILLING_TYPES = ["recurring", "one_time"];

const REMINDER_STAGES = [
  ["d-30", "30 days before"], ["d-14", "14 days before"], ["d-7", "7 days before"],
  ["d-3", "3 days before"], ["d-1", "1 day before"], ["due", "Due today"],
  ["d+1", "1 day overdue"], ["d+3", "3 days overdue"], ["d+5", "5 days overdue"],
  ["d+7", "7 days overdue"],
];

const STATUS_STYLE = {
  active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  due_soon: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  past_due: "bg-red-500/10 text-red-300 border-red-500/30",
  grace_period: "bg-orange-500/10 text-orange-300 border-orange-500/30",
  suspension_eligible: "bg-red-600/10 text-red-400 border-red-600/40",
  suspension_pending: "bg-rose-500/10 text-rose-300 border-rose-500/30",
  suspended: "bg-slate-700/40 text-slate-400 border-slate-600",
  reactivation_pending: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  cancelled: "bg-slate-800 text-slate-500 border-slate-700",
  completed: "bg-emerald-600/15 text-emerald-300 border-emerald-500/40",
};

// -------------------- utilities --------------------
const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = (d) => d.toISOString().slice(0, 10);
const today = () => iso(new Date());

function StatusPill({ status }) {
  const cls = STATUS_STYLE[status] || "bg-slate-800 text-slate-400 border-slate-700";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border ${cls}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

// -------------------- widget filter chip --------------------
function WidgetChip({ label, value, icon: Icon, tone = "blue", filterKey, activeFilter, onClick }) {
  const tones = {
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    red: "text-red-400 bg-red-500/10 border-red-500/30",
  };
  const active = activeFilter === filterKey;
  return (
    <button
      type="button"
      onClick={() => onClick(filterKey)}
      data-testid={`widget-${filterKey}`}
      className={`text-left rounded-xl border p-4 transition-all ${
        active ? "border-blue-400 bg-blue-500/10 ring-1 ring-blue-400/40" : "border-slate-800 bg-slate-900/40 hover:border-slate-600"
      }`}
      aria-pressed={active}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        {Icon ? (
          <span className={`w-8 h-8 grid place-items-center rounded-md border ${tones[tone]}`}>
            <Icon className="w-4 h-4" />
          </span>
        ) : null}
      </div>
      <div className="text-2xl font-bold text-slate-100">{value}</div>
    </button>
  );
}

// -------------------- Service modal --------------------
function ServiceModal({ open, onClose, onCreated, initial = null, companies = [], clients = [] }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    name: "", category: "website_maintenance",
    billing_type: "recurring",
    price: "",
    billing_frequency: "monthly", custom_days: 30,
    next_billing_date: today(),
    invoice_date: today(),
    due_date: today(),
    grace_period_days: 7,
    auto_invoice_enabled: true,
    auto_reminder_enabled: null,   // engine decides default; user override via checkbox
    reminder_stages: null,
    company_id: "", client_id: "", notes: "",
  }));
  const [showAdvancedReminders, setShowAdvancedReminders] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        ...form,
        ...initial,
        auto_reminder_enabled: initial.auto_reminder_enabled ?? null,
        reminder_stages: initial.reminder_stages ?? null,
        price: String(initial.price ?? ""),
        custom_days: initial.custom_days ?? 30,
        company_id: initial.company_id || "", client_id: initial.client_id || "",
        invoice_date: initial.invoice_date || today(),
        due_date: initial.due_date || today(),
      });
      setShowAdvancedReminders(!!initial.reminder_stages);
    } else {
      setForm((f) => ({ ...f, auto_reminder_enabled: null }));
      setShowAdvancedReminders(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const isOneTime = form.billing_type === "one_time";
  // effective default value for auto-reminder checkbox display
  const effectiveAutoReminder =
    form.auto_reminder_enabled === null ? (isOneTime ? false : true) : form.auto_reminder_enabled;

  const clientOptions = useMemo(
    () => (form.company_id ? clients.filter((c) => c.company_id === form.company_id || !c.company_id) : clients),
    [form.company_id, clients]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.price === "") return toast.error("Name and price required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        billing_type: form.billing_type,
        price: parseFloat(form.price),
        currency: "usd",
        company_id: form.company_id || null,
        client_id: form.client_id || null,
        notes: form.notes || null,
        auto_invoice_enabled: !!form.auto_invoice_enabled,
        auto_reminder_enabled: effectiveAutoReminder,
        reminder_stages: showAdvancedReminders && effectiveAutoReminder && form.reminder_stages ? form.reminder_stages : null,
      };
      if (isOneTime) {
        payload.invoice_date = form.invoice_date;
        payload.due_date = form.due_date;
      } else {
        payload.billing_frequency = form.billing_frequency;
        payload.custom_days = form.billing_frequency === "custom" ? parseInt(form.custom_days, 10) : null;
        payload.next_billing_date = form.next_billing_date;
        payload.grace_period_days = parseInt(form.grace_period_days, 10) || 0;
      }
      if (isEdit) {
        await api.patch(`/billing/services/${initial.service_id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/billing/services", payload);
        toast.success("Service created");
      }
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const inputCls = "w-full h-9 bg-slate-900 border border-slate-800 rounded px-2 text-sm text-slate-100";
  const labelCls = "text-[10px] uppercase tracking-widest text-slate-500 block mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" role="dialog" data-testid="svc-modal">
      <form onSubmit={submit} className="w-full max-w-2xl rounded-xl border border-slate-800 bg-slate-950 p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-slate-100">
            {isEdit ? "Edit Service" : "New Service"}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>

        {/* Billing type radio */}
        <div className="grid grid-cols-2 gap-2" data-testid="svc-type-radio">
          {BILLING_TYPES.map((t) => (
            <label key={t} className={`cursor-pointer rounded-lg border p-3 text-sm text-center transition-all ${form.billing_type === t ? "border-blue-500 bg-blue-500/10 text-blue-200" : "border-slate-800 bg-slate-900/50 text-slate-300 hover:border-slate-600"}`}>
              <input type="radio" name="billing_type" className="sr-only" checked={form.billing_type === t} onChange={() => setForm({ ...form, billing_type: t })} data-testid={`svc-type-${t}`} />
              <div className="font-semibold">{t === "recurring" ? "Recurring" : "One-Time"}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t === "recurring" ? "Repeats on a schedule" : "Single invoice, no schedule"}</div>
            </label>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block"><span className={labelCls}>Service name *</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="svc-name" placeholder="e.g. Top of the Line Tree Service — Monthly Maintenance" className={inputCls} /></label>

          <label className="col-span-2 block"><span className={labelCls}>Category *</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="svc-category" className={inputCls}>
              {CATEGORY_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </optgroup>
              ))}
            </select></label>

          <label className="block"><span className={labelCls}>Price (USD) *</span>
            <input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="svc-price" className={inputCls} /></label>

          {isOneTime ? (
            <>
              <label className="block"><span className={labelCls}>Invoice date</span>
                <input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="svc-invoice-date" className={inputCls} /></label>
              <label className="block"><span className={labelCls}>Due date</span>
                <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="svc-due-date" className={inputCls} /></label>
            </>
          ) : (
            <>
              <label className="block"><span className={labelCls}>Frequency</span>
                <select value={form.billing_frequency} onChange={(e) => setForm({ ...form, billing_frequency: e.target.value })} data-testid="svc-freq" className={inputCls}>
                  {FREQUENCIES.map((f) => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
                </select></label>
              {form.billing_frequency === "custom" ? (
                <label className="block"><span className={labelCls}>Custom days</span>
                  <input type="number" min="1" value={form.custom_days} onChange={(e) => setForm({ ...form, custom_days: e.target.value })} className={inputCls} /></label>
              ) : null}
              <label className="block"><span className={labelCls}>Next billing date</span>
                <input type="date" value={form.next_billing_date} onChange={(e) => setForm({ ...form, next_billing_date: e.target.value })} data-testid="svc-next-date" className={inputCls} /></label>
              <label className="block"><span className={labelCls}>Grace period (days)</span>
                <input type="number" min="0" value={form.grace_period_days} onChange={(e) => setForm({ ...form, grace_period_days: e.target.value })} className={inputCls} /></label>
            </>
          )}

          <label className="block"><span className={labelCls}>Company</span>
            <select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value, client_id: "" })} data-testid="svc-company" className={inputCls}>
              <option value="">— none —</option>
              {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </select></label>
          <label className="block"><span className={labelCls}>Client</span>
            <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="svc-client" className={inputCls}>
              <option value="">— none —</option>
              {clientOptions.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
            </select></label>

          <label className="col-span-2 flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={!!form.auto_invoice_enabled} onChange={(e) => setForm({ ...form, auto_invoice_enabled: e.target.checked })} data-testid="svc-auto-inv" />
            Auto-generate invoice{isOneTime ? "" : "s"}
          </label>
          <label className="col-span-2 flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={effectiveAutoReminder} onChange={(e) => setForm({ ...form, auto_reminder_enabled: e.target.checked })} data-testid="svc-auto-rem" />
            Auto-send reminders {isOneTime ? <span className="text-xs text-slate-500 ml-1">(off by default for one-time)</span> : null}
          </label>

          {effectiveAutoReminder ? (
            <div className="col-span-2">
              <button type="button" onClick={() => setShowAdvancedReminders((v) => !v)} className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {showAdvancedReminders ? "Use engine default cadence" : "Customize reminder cadence for this service"}
              </button>
              {showAdvancedReminders ? (
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1.5 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  {REMINDER_STAGES.map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1.5 text-[11px] text-slate-300">
                      <input type="checkbox" checked={(form.reminder_stages || []).includes(k)} onChange={(e) => {
                        const cur = new Set(form.reminder_stages || []);
                        e.target.checked ? cur.add(k) : cur.delete(k);
                        setForm({ ...form, reminder_stages: Array.from(cur) });
                      }} data-testid={`svc-stage-${k}`} />{l}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <label className="col-span-2 block"><span className={labelCls}>Notes</span>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} h-auto py-1.5`} /></label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded border border-slate-700 text-slate-300 text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="svc-save" className="h-9 px-4 rounded bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold disabled:opacity-50">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create service"}
          </button>
        </div>
      </form>
    </div>
  );
}

// -------------------- Service Details Drawer --------------------
function ServiceDrawer({ open, service, onClose, onAction }) {
  const [history, setHistory] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!open || !service) { setHistory(null); return; }
    setTab("overview");
    api.get(`/billing/services/${service.service_id}/history`)
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory({ invoices: [], payments: [], reminders: [], activity: [] }));
  }, [open, service]);

  if (!open || !service) return null;
  const isOneTime = service.billing_type === "one_time";
  const co = history?.service || service;

  return (
    <div className="fixed inset-0 z-40 bg-black/60" onClick={onClose} data-testid="svc-drawer">
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-slate-950 border-l border-slate-800 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="p-5 border-b border-slate-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{CATEGORY_LABELS[co.category] || co.category}</div>
            <h2 className="font-display text-lg font-bold text-slate-100 truncate">{co.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <StatusPill status={co.status} />
              <span className="text-[10px] uppercase tracking-widest text-slate-500">{isOneTime ? "One-time" : co.billing_frequency}</span>
              <span className="text-sm font-semibold text-slate-200 ml-1">{money(co.price)}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </header>

        {/* Actions */}
        <div className="p-4 border-b border-slate-800 flex flex-wrap gap-2">
          <button onClick={() => onAction("edit", co)} data-testid="drawer-edit" className="btn-scale h-8 px-3 rounded border border-slate-700 hover:border-blue-500 text-slate-200 text-xs">Edit</button>
          <button onClick={() => onAction("invoice", co)} data-testid="drawer-invoice" className="btn-scale h-8 px-3 rounded border border-slate-700 hover:border-blue-500 text-slate-200 text-xs inline-flex items-center gap-1"><CreditCard className="w-3 h-3" /> Generate invoice</button>
          <button onClick={() => onAction("reminder", co)} data-testid="drawer-remind" className="btn-scale h-8 px-3 rounded border border-slate-700 hover:border-blue-500 text-slate-200 text-xs inline-flex items-center gap-1"><Bell className="w-3 h-3" /> Send reminder</button>
          {!isOneTime && co.status !== "cancelled" && co.status !== "suspended" ? (
            <button onClick={() => onAction("renew", co)} data-testid="drawer-renew" className="btn-scale h-8 px-3 rounded border border-emerald-600 bg-emerald-500/10 text-emerald-300 text-xs inline-flex items-center gap-1"><RotateCw className="w-3 h-3" /> Renew (advance date)</button>
          ) : null}
          {isOneTime && co.status !== "completed" ? (
            <button onClick={() => onAction("complete", co)} data-testid="drawer-complete" className="btn-scale h-8 px-3 rounded border border-emerald-600 bg-emerald-500/10 text-emerald-300 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Mark completed</button>
          ) : null}
          {co.status === "suspension_pending" ? (
            <button onClick={() => onAction("confirm-suspend", co)} data-testid="drawer-confirm" className="btn-scale h-8 px-3 rounded border border-red-600 bg-red-500/10 text-red-300 text-xs inline-flex items-center gap-1"><ShieldOff className="w-3 h-3" /> Confirm suspension</button>
          ) : co.status === "suspended" ? (
            <button onClick={() => onAction("reactivate", co)} data-testid="drawer-reactivate" className="btn-scale h-8 px-3 rounded border border-emerald-600 bg-emerald-500/10 text-emerald-300 text-xs inline-flex items-center gap-1"><Play className="w-3 h-3" /> Reactivate</button>
          ) : co.status !== "cancelled" && co.status !== "completed" ? (
            <button onClick={() => onAction("suspend", co)} data-testid="drawer-suspend" className="btn-scale h-8 px-3 rounded border border-slate-700 hover:border-red-500 text-red-300 text-xs inline-flex items-center gap-1"><PauseCircle className="w-3 h-3" /> Suspend</button>
          ) : null}
          <button onClick={() => onAction("cancel", co)} data-testid="drawer-cancel" className="btn-scale h-8 px-3 rounded border border-slate-700 hover:border-slate-500 text-slate-400 text-xs ml-auto">Cancel service</button>
        </div>

        {/* Tabs */}
        <nav className="border-b border-slate-800 px-4 flex gap-4 text-xs">
          {[
            ["overview", "Overview"],
            ["invoices", `Invoices (${history?.invoices?.length ?? 0})`],
            ["payments", `Payments (${history?.payments?.length ?? 0})`],
            ["reminders", `Reminders (${history?.reminders?.length ?? 0})`],
            ["activity", `Activity (${history?.activity?.length ?? 0})`],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`py-3 border-b-2 -mb-px ${tab === k ? "border-blue-500 text-blue-300" : "border-transparent text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </nav>

        <div className="flex-1 overflow-y-auto p-5 text-sm text-slate-300">
          {tab === "overview" ? (
            <dl className="grid grid-cols-2 gap-3">
              {[
                ["Category", CATEGORY_LABELS[co.category] || co.category],
                ["Billing type", isOneTime ? "One-time" : "Recurring"],
                ["Company", co.company_name || co.company_id || "—"],
                ["Client", co.client_name || co.client_id || "—"],
                ["Client email", co.client_email || "—"],
                ["Price", money(co.price)],
                ["Frequency", isOneTime ? "n/a" : co.billing_frequency],
                ["Start date", co.start_date || "—"],
                [isOneTime ? "Invoice date" : "Next billing date", (isOneTime ? co.invoice_date : co.next_billing_date) || "—"],
                ["Due date", co.due_date || "—"],
                ["Grace period", isOneTime ? "n/a" : `${co.grace_period_days ?? 0} d`],
                ["Payment status", (co.payment_status || "—").replace(/_/g, " ")],
                ["Auto-invoice", co.auto_invoice_enabled ? "Yes" : "No"],
                ["Auto-reminder", co.auto_reminder_enabled ? "Yes" : "No"],
                ["Last payment", co.last_payment_at || "—"],
                ["Notes", co.notes || "—"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[10px] uppercase tracking-widest text-slate-500">{k}</dt>
                  <dd className="text-slate-200 break-words">{String(v)}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          {tab === "invoices" ? (
            <ul className="space-y-2">
              {(history?.invoices || []).map((i) => (
                <li key={i.invoice_id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center justify-between text-xs">
                  <div><div className="text-slate-200 font-mono">#{i.invoice_number || i.invoice_id.slice(-6)}</div><div className="text-slate-500">{i.title}</div></div>
                  <div className="text-right"><div className="text-slate-100 font-semibold">{money(i.total)}</div><StatusPill status={i.status} /></div>
                </li>
              ))}
              {!history?.invoices?.length ? <li className="text-slate-500 text-sm">No invoices yet.</li> : null}
            </ul>
          ) : null}
          {tab === "payments" ? (
            <ul className="space-y-2">
              {(history?.payments || []).map((p) => (
                <li key={p.session_id || p.stripe_payment_intent_id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-100 font-semibold">{money(p.amount_paid || p.amount_total)}</span>
                    <StatusPill status={p.payment_status || p.status} />
                  </div>
                  <div className="text-slate-500 mt-1 font-mono">{p.stripe_payment_intent_id || p.session_id}</div>
                </li>
              ))}
              {!history?.payments?.length ? <li className="text-slate-500 text-sm">No payments yet.</li> : null}
            </ul>
          ) : null}
          {tab === "reminders" ? (
            <ul className="space-y-2">
              {(history?.reminders || []).map((r) => (
                <li key={r.log_id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-200">{r.stage || r.kind} → {r.recipient || "—"}</span>
                    <StatusPill status={r.status} />
                  </div>
                  <div className="text-slate-500 mt-1">{r.sent_at?.slice(0, 19).replace("T", " ")}</div>
                </li>
              ))}
              {!history?.reminders?.length ? <li className="text-slate-500 text-sm">No reminders logged.</li> : null}
            </ul>
          ) : null}
          {tab === "activity" ? (
            <ul className="space-y-2">
              {(history?.activity || []).map((a) => (
                <li key={a.activity_id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-xs">
                  <div className="text-slate-200">{a.action}</div>
                  <div className="text-slate-500 mt-1">{a.at?.slice(0, 19).replace("T", " ")}</div>
                </li>
              ))}
              {!history?.activity?.length ? <li className="text-slate-500 text-sm">No activity yet.</li> : null}
            </ul>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

// -------------------- Main page --------------------
export default function BillingCenter() {
  const [summary, setSummary] = useState({ widgets: {}, services: [] });
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editService, setEditService] = useState(null);
  const [drawerService, setDrawerService] = useState(null);
  const [testTo, setTestTo] = useState("");

  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "all";
  const q = params.get("q") || "";
  const catF = params.get("category") || "";
  const typeF = params.get("type") || "";
  const freqF = params.get("frequency") || "";
  const statusF = params.get("status") || "";
  const companyF = params.get("company") || "";
  const clientF = params.get("client") || "";
  const fromF = params.get("from") || "";
  const toF = params.get("to") || "";

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) next.delete(k);
      else next.set(k, v);
    });
    setParams(next, { replace: true });
  };
  const resetFilters = () => setParams(new URLSearchParams(), { replace: true });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/summary");
      setSummary(data);
    } catch (e) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/companies").then(({ data }) => setCompanies(data || [])).catch(() => {});
    api.get("/crm/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, []);

  const services = summary.services || [];
  const widgets = summary.widgets || {};

  // -------------------- filtering --------------------
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const companyName = (id) => companies.find((c) => c.company_id === id)?.name || "";
    const clientName = (id) => clients.find((c) => c.client_id === id)?.name || "";
    return services.filter((s) => {
      // Widget-chip filter
      if (filter !== "all") {
        if (filter === "overdue" && !(s.bucket_tags || []).includes("overdue")) return false;
        if (filter === "due_today" && !(s.bucket_tags || []).includes("due_today")) return false;
        if (filter === "grace_period" && !(s.bucket_tags || []).includes("grace_period")) return false;
        if (filter === "suspended" && !(s.bucket_tags || []).includes("suspended")) return false;
        if (filter === "hosting_renewals" && !(s.bucket_tags || []).includes("hosting_renewals")) return false;
        if (filter === "domains_expiring" && !(s.bucket_tags || []).includes("domains_expiring")) return false;
        if (filter === "maintenance_renewals" && !(s.bucket_tags || []).includes("maintenance_renewals")) return false;
      }
      // Column filters
      if (catF && s.category !== catF) return false;
      if (typeF && (s.billing_type || "recurring") !== typeF) return false;
      if (freqF && s.billing_frequency !== freqF) return false;
      if (statusF && s.status !== statusF) return false;
      if (companyF && s.company_id !== companyF) return false;
      if (clientF && s.client_id !== clientF) return false;
      if (fromF && (s.next_billing_date || "") < fromF) return false;
      if (toF && (s.next_billing_date || "") > toF) return false;
      // Search
      if (term) {
        const bag = `${s.name || ""} ${companyName(s.company_id)} ${clientName(s.client_id)}`.toLowerCase();
        if (!bag.includes(term)) return false;
      }
      return true;
    });
  }, [services, filter, catF, typeF, freqF, statusF, companyF, clientF, fromF, toF, q, companies, clients]);

  const activeChipLabel = {
    all: "All services", overdue: "Overdue", due_today: "Due today",
    grace_period: "Grace period", suspended: "Suspended / Pending",
    hosting_renewals: "Hosting renewals — next 30 days",
    domains_expiring: "Domain renewals — next 30 days",
    maintenance_renewals: "Maintenance renewals — next 30 days",
  }[filter];

  const hasAnyFilter = filter !== "all" || catF || typeF || freqF || statusF || companyF || clientF || fromF || toF || q;

  // -------------------- actions --------------------
  const onAction = async (kind, svc) => {
    try {
      if (kind === "reminder") await api.post(`/billing/services/${svc.service_id}/send-reminder`);
      if (kind === "invoice") await api.post(`/billing/services/${svc.service_id}/generate-invoice`);
      if (kind === "suspend") await api.post(`/billing/services/${svc.service_id}/suspend`);
      if (kind === "confirm-suspend") await api.post(`/billing/services/${svc.service_id}/confirm-suspension`);
      if (kind === "reactivate") await api.post(`/billing/services/${svc.service_id}/reactivate`);
      if (kind === "renew") await api.post(`/billing/services/${svc.service_id}/renew`);
      if (kind === "complete") await api.post(`/billing/services/${svc.service_id}/complete`);
      if (kind === "cancel") await api.delete(`/billing/services/${svc.service_id}`);
      if (kind === "edit") { setEditService(svc); setCreateOpen(true); return; }
      toast.success("Done");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const runCycle = async () => {
    try {
      const { data } = await api.post("/billing/run-cycle");
      toast.success(`Cycle: +${data.invoices_created} inv · ${data.reminders_sent} sent · ${data.status_updates} status updates`);
      load();
    } catch { toast.error("Cycle failed"); }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Email required");
    try { await api.post("/billing/test-email", { to: testTo }); toast.success(`Test email queued to ${testTo}`); }
    catch { toast.error("Test email failed"); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="billing-center">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-100">Billing & Renewals Center</h1>
            <p className="text-sm text-slate-400">One row per service. Click a widget above to filter · Reset to see everything.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={runCycle} data-testid="run-cycle-btn" className="btn-scale h-9 px-3 rounded-md border border-slate-700 hover:border-blue-500 text-slate-200 text-sm inline-flex items-center gap-2"><RefreshCcw className="w-4 h-4" /> Run cycle now</button>
            <button onClick={() => { setEditService(null); setCreateOpen(true); }} data-testid="new-service-btn" className="btn-scale h-9 px-3 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Service</button>
          </div>
        </div>

        {/* Widgets (top row = counters, second row = filter chips) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="widgets-row-metrics">
          <WidgetChip label="MRR" value={money(widgets.mrr)} icon={DollarSign} tone="blue" filterKey="all" activeFilter={filter} onClick={(k) => setParam({ filter: k === "all" ? "" : k })} />
          <WidgetChip label="Paid this month" value={money(widgets.paid_this_month)} icon={CreditCard} tone="emerald" filterKey="__paid" activeFilter={null} onClick={() => {}} />
          <WidgetChip label="Outstanding" value={money(widgets.outstanding_balance)} icon={Clock} tone="amber" filterKey="__outstanding" activeFilter={null} onClick={() => {}} />
          <WidgetChip label="Overdue" value={money(widgets.overdue_balance)} icon={AlertCircle} tone="red" filterKey="overdue" activeFilter={filter} onClick={(k) => setParam({ filter: k })} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="widgets-row-filters">
          <WidgetChip label="Due today" value={widgets.invoices_due_today || 0} icon={Clock} tone="amber" filterKey="due_today" activeFilter={filter} onClick={(k) => setParam({ filter: k })} />
          <WidgetChip label="Hosting 30d" value={widgets.hosting_renewals || 0} icon={Server} filterKey="hosting_renewals" activeFilter={filter} onClick={(k) => setParam({ filter: k })} />
          <WidgetChip label="Domains 30d" value={widgets.domain_renewals || 0} icon={Globe2} filterKey="domains_expiring" activeFilter={filter} onClick={(k) => setParam({ filter: k })} />
          <WidgetChip label="Maintenance 30d" value={widgets.maintenance_renewals || 0} icon={Wrench} filterKey="maintenance_renewals" activeFilter={filter} onClick={(k) => setParam({ filter: k })} />
          <WidgetChip label="In grace" value={widgets.services_in_grace || 0} icon={PauseCircle} tone="amber" filterKey="grace_period" activeFilter={filter} onClick={(k) => setParam({ filter: k })} />
          <WidgetChip label="Suspended" value={widgets.services_suspended || 0} icon={PowerOff} tone="red" filterKey="suspended" activeFilter={filter} onClick={(k) => setParam({ filter: k })} />
        </div>

        {/* Test email tool */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-4 flex items-center gap-3 flex-wrap" data-testid="test-email-block">
          <Mail className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-300">Send test email:</span>
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yosimediastudio.com" data-testid="test-email-input" className="h-9 w-64 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-slate-100" />
          <button onClick={sendTest} data-testid="test-email-send" className="btn-scale h-9 px-3 rounded-md border border-slate-700 hover:border-blue-500 text-slate-200 text-sm inline-flex items-center gap-2"><Send className="w-4 h-4" /> Send test</button>
        </div>

        {/* Filter bar */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-3 space-y-3" data-testid="filter-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setParam({ q: e.target.value })} placeholder="Search by service, company, or client…" data-testid="filter-search" className="w-full h-9 bg-slate-950 border border-slate-800 rounded pl-8 pr-2 text-sm text-slate-100" />
            </div>
            <select value={typeF} onChange={(e) => setParam({ type: e.target.value })} data-testid="filter-type" className="h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-slate-100">
              <option value="">All types</option><option value="recurring">Recurring</option><option value="one_time">One-Time</option>
            </select>
            <select value={freqF} onChange={(e) => setParam({ frequency: e.target.value })} data-testid="filter-freq" className="h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-slate-100">
              <option value="">All frequencies</option>{FREQUENCIES.map((f) => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
            </select>
            <select value={statusF} onChange={(e) => setParam({ status: e.target.value })} data-testid="filter-status" className="h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-slate-100">
              <option value="">All statuses</option>
              {Object.keys(STATUS_STYLE).map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
            </select>
            <select value={catF} onChange={(e) => setParam({ category: e.target.value })} data-testid="filter-category" className="h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-slate-100 max-w-[180px]">
              <option value="">All categories</option>
              {CATEGORY_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </optgroup>
              ))}
            </select>
            <select value={companyF} onChange={(e) => setParam({ company: e.target.value })} data-testid="filter-company" className="h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-slate-100 max-w-[160px]">
              <option value="">All companies</option>
              {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </select>
            <select value={clientF} onChange={(e) => setParam({ client: e.target.value })} data-testid="filter-client" className="h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-slate-100 max-w-[160px]">
              <option value="">All clients</option>
              {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
            </select>
            <input type="date" value={fromF} onChange={(e) => setParam({ from: e.target.value })} data-testid="filter-from" className="h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-slate-100" />
            <input type="date" value={toF} onChange={(e) => setParam({ to: e.target.value })} data-testid="filter-to" className="h-9 bg-slate-950 border border-slate-800 rounded px-2 text-sm text-slate-100" />
            {hasAnyFilter ? (
              <button onClick={resetFilters} data-testid="filter-reset" className="h-9 px-3 rounded border border-slate-700 hover:border-red-500 text-red-300 text-sm inline-flex items-center gap-1"><X className="w-3 h-3" /> Reset filters</button>
            ) : null}
          </div>
          {filter !== "all" ? (
            <div className="text-xs text-slate-300 flex items-center gap-2" data-testid="active-filter-pill">
              <ListFilter className="w-3 h-3 text-blue-400" />
              <span className="text-slate-500 uppercase tracking-widest text-[10px]">Filter:</span>
              <span className="text-blue-300 font-semibold">{activeChipLabel}</span>
              <button onClick={() => setParam({ filter: "" })} data-testid="filter-clear-chip" className="text-slate-400 hover:text-red-300 text-[11px] underline">clear</button>
            </div>
          ) : null}
        </div>

        {/* Master table */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/30 overflow-hidden" data-testid="master-table">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-100">All services <span className="text-slate-500 text-xs">({filtered.length} shown · {services.length} total)</span></h3>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
          ) : !filtered.length ? (
            <div className="p-10 text-center text-slate-500 text-sm">
              {services.length === 0 ? "No services yet. Click New Service to add your first one." : "No services match the current filters."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-950/60">
                  <tr>
                    <th className="p-3">Service</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">Company</th>
                    <th className="p-3">Client</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Price</th>
                    <th className="p-3">Frequency</th>
                    <th className="p-3">Next date</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Payment</th>
                    <th className="p-3 text-center">Auto</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const isOne = s.billing_type === "one_time";
                    const isVirtual = !!s._virtual;
                    const companyName = s.company_name || companies.find((c) => c.company_id === s.company_id)?.name;
                    const clientName = s.client_name || clients.find((c) => c.client_id === s.client_id)?.name;
                    const payStyle = {
                      paid: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
                      overdue: "bg-red-500/10 text-red-300 border-red-500/30",
                      pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
                      no_invoice: "bg-slate-800 text-slate-400 border-slate-700",
                    }[s.payment_status || "no_invoice"];
                    const payLabel = {
                      paid: "Paid",
                      overdue: "Overdue",
                      pending: "Pending",
                      no_invoice: "No invoice",
                    }[s.payment_status || "no_invoice"];
                    return (
                      <tr key={s.service_id} className={`border-b border-slate-800/70 hover:bg-slate-800/30 ${isVirtual ? "" : "cursor-pointer"}`} onClick={() => !isVirtual && setDrawerService(s)} data-testid={`svc-row-${s.service_id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-slate-100 max-w-[220px] truncate" title={s.name}>{s.name}</div>
                            {isVirtual ? <span data-testid={`svc-virtual-${s.service_id}`} title="Legacy invoice with no linked service — read-only" className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded border bg-amber-500/10 text-amber-300 border-amber-500/30">Legacy</span> : null}
                          </div>
                          <div className="text-[11px] text-slate-500">{isOne ? (s.invoice_date || "").slice(0, 10) : ""}</div>
                        </td>
                        <td className="p-3 text-xs text-slate-400">{CATEGORY_LABELS[s.category] || s.category}</td>
                        <td className="p-3 text-xs text-slate-300 max-w-[150px] truncate" title={companyName || ""}>
                          {companyName ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setParam({ company: s.company_id }); }}
                              data-testid={`svc-co-link-${s.service_id}`}
                              className="hover:text-blue-300 underline decoration-dotted underline-offset-2"
                            >{companyName}</button>
                          ) : <span className="text-slate-500">—</span>}
                        </td>
                        <td className="p-3 text-xs text-slate-300 max-w-[140px] truncate" title={clientName || ""}>
                          {clientName ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setParam({ client: s.client_id }); }}
                              data-testid={`svc-cli-link-${s.service_id}`}
                              className="hover:text-blue-300 underline decoration-dotted underline-offset-2"
                            >{clientName}</button>
                          ) : <span className="text-slate-500">—</span>}
                        </td>
                        <td className="p-3">
                          <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${isOne ? "bg-indigo-500/10 text-indigo-300 border-indigo-500/30" : "bg-blue-500/10 text-blue-300 border-blue-500/30"}`}>
                            {isOne ? "One-time" : "Recurring"}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-slate-200">{money(s.price)}</td>
                        <td className="p-3 text-xs text-slate-400">{isOne ? "—" : (s.billing_frequency || "").replace(/_/g, " ")}</td>
                        <td className="p-3 text-sm text-slate-300">{(isOne ? s.due_date : s.next_billing_date)?.slice(0, 10) || "—"}</td>
                        <td className="p-3"><StatusPill status={s.status} /></td>
                        <td className="p-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border ${payStyle}`} data-testid={`svc-pay-${s.service_id}`}>{payLabel}</span>
                        </td>
                        <td className="p-3 text-center text-[10px] uppercase tracking-widest">
                          {s.auto_invoice_enabled ? <span className="text-emerald-400 mr-1">Inv</span> : null}
                          {s.auto_reminder_enabled ? <span className="text-emerald-400">Rem</span> : null}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          {isVirtual ? (
                            <>
                              <button onClick={() => api.post(`/billing/invoices/${s._source_invoice_id}/send-reminder`).then(() => toast.success("Reminder sent")).catch((e) => toast.error(e.response?.data?.detail || "Failed"))} data-testid={`svc-remind-${s.service_id}`} title="Send reminder for this invoice" className="btn-scale inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-700 hover:border-blue-500 text-xs text-slate-200 mr-1"><Bell className="w-3 h-3" /></button>
                              <a href={`/app/invoices?id=${s._source_invoice_id}`} title="Open invoice in Invoices module" onClick={(e) => e.stopPropagation()} className="btn-scale inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-700 hover:border-blue-500 text-xs text-slate-200"><CreditCard className="w-3 h-3" /></a>
                            </>
                          ) : (
                            <>
                              <button onClick={() => onAction("reminder", s)} data-testid={`svc-remind-${s.service_id}`} title="Send reminder" className="btn-scale inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-700 hover:border-blue-500 text-xs text-slate-200 mr-1"><Bell className="w-3 h-3" /></button>
                              <button onClick={() => onAction("invoice", s)} data-testid={`svc-inv-${s.service_id}`} title="Generate invoice" className="btn-scale inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-700 hover:border-blue-500 text-xs text-slate-200 mr-1"><CreditCard className="w-3 h-3" /></button>
                              {!isOne && s.status !== "cancelled" && s.status !== "suspended" ? (
                                <button onClick={() => onAction("renew", s)} data-testid={`svc-renew-${s.service_id}`} title="Renew (advance next billing date)" className="btn-scale inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-700 hover:border-emerald-500 text-xs text-emerald-300 mr-1"><RotateCw className="w-3 h-3" /></button>
                              ) : null}
                              {isOne && s.status !== "completed" ? (
                                <button onClick={() => onAction("complete", s)} data-testid={`svc-complete-${s.service_id}`} title="Mark completed" className="btn-scale inline-flex items-center gap-1 h-7 px-2 rounded border border-emerald-600 bg-emerald-500/10 text-xs text-emerald-300"><CheckCircle2 className="w-3 h-3" /></button>
                              ) : s.status === "suspension_pending" ? (
                                <button onClick={() => onAction("confirm-suspend", s)} data-testid={`svc-conf-${s.service_id}`} title="Confirm suspension" className="btn-scale inline-flex items-center gap-1 h-7 px-2 rounded border border-red-600 bg-red-500/10 text-xs text-red-300"><ShieldOff className="w-3 h-3" /></button>
                              ) : s.status === "suspended" ? (
                                <button onClick={() => onAction("reactivate", s)} data-testid={`svc-react-${s.service_id}`} title="Reactivate" className="btn-scale inline-flex items-center gap-1 h-7 px-2 rounded border border-emerald-600 bg-emerald-500/10 text-xs text-emerald-300"><Play className="w-3 h-3" /></button>
                              ) : s.status !== "cancelled" && s.status !== "completed" ? (
                                <button onClick={() => onAction("suspend", s)} data-testid={`svc-susp-${s.service_id}`} title="Suspend" className="btn-scale inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-700 hover:border-red-500 text-xs text-red-300"><PauseCircle className="w-3 h-3" /></button>
                              ) : null}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <ServiceModal
          open={createOpen}
          initial={editService}
          companies={companies}
          clients={clients}
          onClose={() => { setCreateOpen(false); setEditService(null); }}
          onCreated={() => load()}
        />
        <ServiceDrawer
          open={!!drawerService}
          service={drawerService}
          onClose={() => setDrawerService(null)}
          onAction={async (kind, svc) => {
            if (kind === "edit") { setDrawerService(null); setEditService(svc); setCreateOpen(true); return; }
            await onAction(kind, svc);
            // If service was cancelled/completed, close drawer; else refresh drawer data
            if (["cancel", "complete"].includes(kind)) setDrawerService(null);
            else { const { data } = await api.get(`/billing/services/${svc.service_id}`); setDrawerService(data); }
          }}
        />
      </div>
    </DashboardLayout>
  );
}
